import { SegmentKey } from '@deltasierra/i18n';
import { HasId, HttpMethod, ApiRoute } from '../common';
import { SocketMessage, SocketMessageEvent } from '../sockets/messages';
import { TranslateOptions } from '../i18n';
import { UserId } from '../user/User';

export const UNREAD_NOTIFICATIONS_LIMIT = 5;
export const GET_NOTIFICATIONS_LIMIT = 10;

export type InAppNotificationType =
    | 'Agency.CustomMessage'
    | 'EmailPublishFailed'
    | 'EmailPublishSuccess'
    | 'ImagePublishFailed'
    | 'IntegrationConnectivityFailure'
    | 'MapGenerated'
    | 'MediaTemplateReadyToDownload'
    | 'ScheduledPost.NoNotifiableDevices'
    | 'TestInAppNotifications'
    | 'VideoPublishedExternally'
    | 'VideoPublishedLocally'
    | 'VideoPublishFailed';

export type InAppNotificationSeverity = 'danger' | 'info' | 'success' | 'warning';

interface BaseInAppNotification {
    userId: UserId;
    link: string | null;
    type: InAppNotificationType;
    severity: InAppNotificationSeverity;
    groupingId?: string | null;
}

interface ReadableInAppNotification extends HasId, BaseInAppNotification {
    createdAt: Date;
    readAt?: Date | null;
    clickedAt?: Date | null;
}

interface TranslationProperties {
    messageKey: SegmentKey;
    messageOptions: TranslateOptions;
    subjectKey: SegmentKey | null;
    subjectOptions: TranslateOptions | null;
}

export interface CreateInAppNotification extends BaseInAppNotification, TranslationProperties { }

export interface InAppNotification extends ReadableInAppNotification, TranslationProperties { }

export interface InAppNotificationTranslated extends ReadableInAppNotification {
    message: string;
    subject: string | null;
}

export interface GetUnreadNotificationsResponse {
    recent: InAppNotification[];
    count: number;
}

export type InAppNotificationSocketMessage = SocketMessage<InAppNotification>;
export const inAppNotificationSocketEvent = new SocketMessageEvent<InAppNotification>('userNotification');

const NOTIFICATIONS_API_ROOT = 'notifications';

export interface IGetInAppNotificationsQuery {
    previousId?: number;
    previousDate?: Date;
}

export interface BaseInAppNotificationParams {
    inAppNotificationId: number;
}

export interface UnreadCountResponse {
    count: number;
}

export const InAppNotificationsApi = {
    getNotification: new ApiRoute<InAppNotification, BaseInAppNotificationParams, void, void>(HttpMethod.Get, path =>
        path.literal(NOTIFICATIONS_API_ROOT).param('inAppNotificationId'),
    ),
    getNotificationTranslated: new ApiRoute<InAppNotificationTranslated, BaseInAppNotificationParams, void, void>(
        HttpMethod.Get,
        path => path.literal(NOTIFICATIONS_API_ROOT).param('inAppNotificationId').literal('translated'),
    ),
    getNotifications: new ApiRoute<InAppNotification[], void, void, IGetInAppNotificationsQuery>(HttpMethod.Get, path =>
        path.literal(NOTIFICATIONS_API_ROOT),
    ),
    getNotificationsTranslated: new ApiRoute<InAppNotificationTranslated[], void, void, IGetInAppNotificationsQuery>(
        HttpMethod.Get,
        path => path.literal(NOTIFICATIONS_API_ROOT).literal('translated'),
    ),
    getUnread: new ApiRoute<GetUnreadNotificationsResponse, void, void, void>(HttpMethod.Get, path =>
        path.literal(NOTIFICATIONS_API_ROOT).literal('unread'),
    ),
    getUnreadCount: new ApiRoute<UnreadCountResponse, void, void, void>(HttpMethod.Get, path =>
        path.literal(NOTIFICATIONS_API_ROOT).literal('unread').literal('count'),
    ),
    markAllAsRead: new ApiRoute<void, void, void, void>(HttpMethod.Post, path =>
        path.literal(NOTIFICATIONS_API_ROOT).literal('readAll'),
    ),
    markAsRead: new ApiRoute<void, BaseInAppNotificationParams, void, void>(HttpMethod.Post, path =>
        path.literal(NOTIFICATIONS_API_ROOT).param('inAppNotificationId').literal('read'),
    ),
    recordClicked: new ApiRoute<void, BaseInAppNotificationParams, void, void>(HttpMethod.Post, path =>
        path.literal(NOTIFICATIONS_API_ROOT).param('inAppNotificationId').literal('clicked'),
    ),
};
